import { trendSeriesKeys } from "./trend-metrics";

/** Ad account kinds. `google_ads_customer` is the stored kind for Google Ads. */
export const ADS_KINDS = new Set([
  "facebook_ads_account",
  "google_ads",
  "google_ads_customer",
  "linkedin_ads_account",
  "tiktok_ads_account",
]);

export const TABS = [
  { id: "summary", label: "Summary" },
  { id: "ads", label: "Ads" },
  { id: "social", label: "Social" },
  { id: "website", label: "Website" },
  { id: "search", label: "Search" },
];

export const TREND_METRICS = {
  summary: [
    { key: "visitors", label: "Visitors" },
    { key: "reach", label: "Reach" },
    { key: "adSpend", label: "Ad spend" },
    { key: "conversions", label: "Conversions" },
    { key: "followers", label: "Followers" },
  ],
  ads: [
    { key: "spend", label: "Spend" },
    { key: "impressions", label: "Impressions" },
    { key: "clicks", label: "Clicks" },
    { key: "conversions", label: "Conversions" },
  ],
  social: [
    { key: "reach", label: "Reach" },
    { key: "views", label: "Views" },
    { key: "engagements", label: "Engagements" },
    { key: "followers", label: "Followers" },
  ],
  website: [
    { key: "users", label: "Users" },
    { key: "sessions", label: "Sessions" },
    { key: "pageViews", label: "Page views" },
    { key: "conversions", label: "Conversions" },
  ],
  search: [
    { key: "clicks", label: "Clicks" },
    { key: "impressions", label: "Impressions" },
  ],
};

const HELP = {
  visitors: "People who visited your website. Google Analytics counts each person once in the range.",
  reach: "Unique Facebook and Instagram accounts that saw your content.",
  adSpend: "Money spent on connected ad accounts in this range.",
  conversions: "Website goal completions plus conversions reported by ad accounts.",
  followers: "Facebook Page fans plus Instagram followers at the latest point in the range.",
  spend: "Total ad spend in this range.",
  impressions: "Times your ads were shown.",
  clicks: "Clicks on your ads.",
  ctr: "Click-through rate: ad clicks divided by impressions.",
  cpc: "Average amount you paid for each ad click. Lower is better.",
  adsConversions: "Conversions reported by your ad accounts.",
  costPerConversion: "Ad spend divided by conversions. Lower is better.",
  roas: "Return on ad spend: conversion value divided by spend.",
  socialFollowers: "Followers across Facebook, Instagram, and YouTube channels in this tab.",
  socialReach: "Unique accounts reached on Facebook and Instagram.",
  views: "Times your posts or videos were viewed (Instagram views, Facebook impressions, YouTube views).",
  engagements: "Likes, comments, shares, and other interactions.",
  users: "People who visited your website.",
  sessions: "Visits to your website. One person can start more than one session.",
  pageViews: "Pages loaded on your website.",
  bounceRate: "Share of sessions that left without another interaction. Lower is better.",
  averageSessionDuration: "Average time people stayed on your site.",
  webConversions: "Goal completions recorded in Google Analytics.",
  searchClicks: "Clicks from Google Search results to your site.",
  searchImpressions: "Times your pages appeared in Google Search.",
  searchCtr: "Search click-through rate: clicks divided by impressions.",
  position: "Average Google Search ranking. A lower number means a higher rank.",
};

export function tabForKind(kind) {
  if (ADS_KINDS.has(kind)) return "ads";
  if (kind === "facebook_page" || kind === "instagram_business" || kind === "youtube_channel") return "social";
  if (kind === "ga4_property") return "website";
  if (kind === "search_console_site") return "search";
  return null;
}

export function dayKey(value) {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return String(value).slice(0, 10);
}

function addDays(isoDate, days) {
  const date = new Date(`${isoDate}T00:00:00`);
  date.setDate(date.getDate() + days);
  return dayKey(date);
}

function inclusiveDays(start, end) {
  const ms = new Date(`${end}T00:00:00`) - new Date(`${start}T00:00:00`);
  return Math.round(ms / 86400000) + 1;
}

export function resolveRange({ preset = "30d", customStart, customEnd, today = new Date() } = {}) {
  const end = dayKey(today);
  if (preset === "7d" || preset === "30d" || preset === "90d") {
    const days = preset === "7d" ? 7 : preset === "90d" ? 90 : 30;
    return { preset, start: addDays(end, -(days - 1)), end, days, invalid: false };
  }
  const start = dayKey(customStart);
  const customTo = dayKey(customEnd);
  if (!start || !customTo || start > customTo) {
    return { preset: "custom", start: end, end, days: 1, invalid: true };
  }
  return {
    preset: "custom",
    start,
    end: customTo,
    days: inclusiveDays(start, customTo),
    invalid: false,
  };
}

export function previousRange(range) {
  const end = addDays(range.start, -1);
  const start = addDays(end, -(range.days - 1));
  return { preset: null, start, end, days: range.days, invalid: false };
}

export function syncWindowForRange(range) {
  if (range?.preset === "7d" || range?.preset === "30d" || range?.preset === "90d") return range.preset;
  if ((range?.days || 30) <= 7) return "7d";
  if (range.days <= 30) return "30d";
  return "90d";
}

export function percentChange(current, previous) {
  if (current == null || previous == null) return null;
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

function accountKind(account) {
  return account?.connectedAccount?.providerAccountKind || "";
}

export function seriesKeysForMetric(account, metric) {
  const kind = accountKind(account);
  if (metric === "visitors" || metric === "users") return ["users"];
  if (metric === "adSpend" || metric === "spend") return ["spend", "cost"];
  if (metric === "views") return ["views", "impressions"];
  if (metric === "reach" || metric === "socialReach") return trendSeriesKeys(account, "reach");
  if (metric === "impressions" || metric === "searchImpressions") {
    if (kind === "ga4_property") return ["pageViews"];
    if (kind === "instagram_business") return ["views", "impressions"];
    return ["impressions"];
  }
  if (metric === "engagements") {
    if (kind === "ga4_property") return ["engagedSessions", "engagements"];
    return ["engagements"];
  }
  if (metric === "followers" || metric === "socialFollowers") return ["followers"];
  if (metric === "clicks" || metric === "searchClicks") return ["clicks"];
  if (metric === "pageViews") return ["pageViews"];
  if (metric === "sessions") return ["sessions"];
  if (metric === "conversions" || metric === "webConversions" || metric === "adsConversions") {
    return ["conversions"];
  }
  if (metric === "ctr" || metric === "searchCtr") return ["ctr"];
  if (metric === "position") return ["position"];
  if (metric === "cpc") return ["cpc"];
  if (metric === "roas") return ["roas"];
  if (metric === "bounceRate") return ["bounceRate"];
  if (metric === "averageSessionDuration") return ["averageSessionDuration", "avgSessionDuration"];
  return [metric];
}

function pointsInRange(points, range) {
  if (!Array.isArray(points) || !range?.start || !range?.end) return [];
  return points.filter((point) => {
    const day = dayKey(point?.date);
    return day && day >= range.start && day <= range.end;
  });
}

function firstPoints(account, keys, range) {
  for (const key of keys) {
    const matched = pointsInRange(account?.series?.[key], range);
    if (matched.length) return matched;
  }
  return [];
}

function snapshotNumber(account, keys, range, allowSnapshot) {
  if (!allowSnapshot || !range?.preset || account?.window !== range.preset) return null;
  const metrics = account?.metrics || {};
  for (const key of keys) {
    if (metrics[key] != null && metrics[key] !== "") {
      const value = Number(metrics[key]);
      return Number.isFinite(value) ? value : null;
    }
  }
  return null;
}

export function aggregateFlow(accounts, keys, range, { allowSnapshot = false } = {}) {
  let total = 0;
  let any = false;
  for (const account of accounts || []) {
    const points = firstPoints(account, keys, range);
    if (points.length) {
      total += points.reduce((sum, point) => sum + Number(point.value || 0), 0);
      any = true;
      continue;
    }
    const fallback = snapshotNumber(account, keys, range, allowSnapshot);
    if (fallback != null) {
      total += fallback;
      any = true;
    }
  }
  return any ? total : null;
}

export function aggregateStock(accounts, keys, range, { allowSnapshot = false } = {}) {
  let total = 0;
  let any = false;
  for (const account of accounts || []) {
    const points = firstPoints(account, keys, range);
    if (points.length) {
      const latest = [...points].sort((a, b) => dayKey(a.date).localeCompare(dayKey(b.date))).at(-1);
      total += Number(latest?.value || 0);
      any = true;
      continue;
    }
    const fallback = snapshotNumber(account, keys, range, allowSnapshot);
    if (fallback != null) {
      total += fallback;
      any = true;
    }
  }
  return any ? total : null;
}

export function aggregateWeighted(accounts, rateKeys, weightKeys, range, { allowSnapshot = false } = {}) {
  let weighted = 0;
  let weight = 0;
  for (const account of accounts || []) {
    const rates = firstPoints(account, rateKeys, range);
    const weights = firstPoints(account, weightKeys, range);
    if (rates.length) {
      const weightByDay = new Map(weights.map((point) => [dayKey(point.date), Number(point.value || 0)]));
      let localWeighted = 0;
      let localWeight = 0;
      for (const point of rates) {
        const dayWeight = weightByDay.get(dayKey(point.date)) || 0;
        if (dayWeight <= 0) continue;
        localWeighted += Number(point.value || 0) * dayWeight;
        localWeight += dayWeight;
      }
      if (localWeight > 0) {
        weighted += localWeighted;
        weight += localWeight;
      } else {
        const average = rates.reduce((sum, point) => sum + Number(point.value || 0), 0) / rates.length;
        weighted += average;
        weight += 1;
      }
      continue;
    }
    const rate = snapshotNumber(account, rateKeys, range, allowSnapshot);
    if (rate == null) continue;
    const snapWeight = snapshotNumber(account, weightKeys, range, allowSnapshot);
    const applied = snapWeight != null && snapWeight > 0 ? snapWeight : 1;
    weighted += rate * applied;
    weight += applied;
  }
  if (weight <= 0) return null;
  return weighted / weight;
}

function aggregateRatio(accounts, numeratorKeys, denominatorKeys, range, scale, allowSnapshot) {
  const numerator = aggregateFlow(accounts, numeratorKeys, range, { allowSnapshot });
  const denominator = aggregateFlow(accounts, denominatorKeys, range, { allowSnapshot });
  if (numerator == null || denominator == null || denominator === 0) return null;
  return (numerator / denominator) * scale;
}

function paired(read) {
  return (accounts, range) => {
    const current = read(accounts, range, true);
    const previous = read(accounts, previousRange(range), false);
    return { current, previous, change: percentChange(current, previous) };
  };
}

function flowPair(keys) {
  return paired((accounts, range, allowSnapshot) => aggregateFlow(accounts, keys, range, { allowSnapshot }));
}

function stockPair(keys) {
  return paired((accounts, range, allowSnapshot) => aggregateStock(accounts, keys, range, { allowSnapshot }));
}

function ratioPair(numeratorKeys, denominatorKeys, scale = 1) {
  return paired((accounts, range, allowSnapshot) => (
    aggregateRatio(accounts, numeratorKeys, denominatorKeys, range, scale, allowSnapshot)
  ));
}

function weightedPair(rateKeys, weightKeys) {
  return paired((accounts, range, allowSnapshot) => (
    aggregateWeighted(accounts, rateKeys, weightKeys, range, { allowSnapshot })
  ));
}

function addNullable(left, right) {
  if (left == null && right == null) return null;
  return Number(left || 0) + Number(right || 0);
}

function kpi(spec, stats) {
  return {
    tone: "higher-better",
    format: "number",
    ...spec,
    current: stats.current,
    previous: stats.previous,
    change: stats.change,
  };
}

export function groupAccounts(accounts) {
  const grouped = { ads: [], social: [], website: [], search: [], other: [] };
  for (const account of accounts || []) {
    const tab = tabForKind(account?.connectedAccount?.providerAccountKind);
    (tab ? grouped[tab] : grouped.other).push(account);
  }
  return grouped;
}

export function mergeConnection(entry, integration) {
  const account = entry?.connectedAccount || {};
  return {
    ...entry,
    connectedAccount: {
      ...account,
      providerAccountId: integration?.providerAccountId || account.providerAccountId || null,
      status: integration?.status || account.status || null,
      lastError: integration?.lastError || account.lastError || null,
      syncPaused: Boolean(integration?.syncPaused ?? account.syncPaused),
    },
  };
}

export function hydrateAccounts(perAccount, connections) {
  const byId = new Map((connections || []).map((row) => [row.id, row]));
  const items = (perAccount || [])
    .map((entry) => mergeConnection(entry, byId.get(entry?.connectedAccount?.id)))
    .filter((entry) => entry.connectedAccount?.status !== "disconnected");
  const seen = new Set(items.map((entry) => entry.connectedAccount?.id).filter(Boolean));

  for (const connection of connections || []) {
    if (!connection?.id || seen.has(connection.id)) continue;
    if (connection.status === "disconnected") continue;
    if (!tabForKind(connection.providerAccountKind)) continue;
    items.push({
      connectedAccount: {
        id: connection.id,
        provider: connection.provider,
        providerAccountKind: connection.providerAccountKind,
        providerAccountId: connection.providerAccountId,
        displayName: connection.displayName,
        avatarUrl: connection.avatarUrl,
        status: connection.status,
        lastError: connection.lastError,
        syncPaused: Boolean(connection.syncPaused),
      },
      window: connection.defaultWindow || null,
      metrics: null,
      series: {},
      topPosts: [],
      topChannels: [],
    });
  }
  return items;
}

export function filterByAccount(accounts, accountId) {
  if (!accountId || accountId === "all") return accounts || [];
  return (accounts || []).filter((entry) => entry.connectedAccount?.id === accountId);
}

export function buildSummaryKpis(accounts, range) {
  const grouped = groupAccounts(accounts);
  const visitors = flowPair(["users"])(grouped.website, range);
  const reach = flowPair(["reach"])(grouped.social, range);
  const adSpend = flowPair(["spend", "cost"])(grouped.ads, range);
  const webConversions = flowPair(["conversions"])(grouped.website, range);
  const adConversions = flowPair(["conversions"])(grouped.ads, range);
  const conversions = {
    current: addNullable(webConversions.current, adConversions.current),
    previous: addNullable(webConversions.previous, adConversions.previous),
  };
  conversions.change = percentChange(conversions.current, conversions.previous);
  const followers = stockPair(["followers"])(grouped.social, range);

  return [
    kpi({ key: "visitors", label: "Visitors", help: HELP.visitors, accent: "indigo" }, visitors),
    kpi({ key: "reach", label: "Reach", help: HELP.reach, accent: "sky" }, reach),
    kpi({ key: "adSpend", label: "Ad spend", help: HELP.adSpend, format: "money", tone: "neutral", accent: "amber" }, adSpend),
    kpi({ key: "conversions", label: "Conversions", help: HELP.conversions, accent: "emerald" }, conversions),
    kpi({ key: "followers", label: "Followers", help: HELP.followers, accent: "violet" }, followers),
  ];
}

export function buildAdsKpis(accounts, range) {
  const spend = flowPair(["spend", "cost"])(accounts, range);
  const impressions = flowPair(["impressions"])(accounts, range);
  const clicks = flowPair(["clicks"])(accounts, range);
  const conversions = flowPair(["conversions"])(accounts, range);
  const revenue = flowPair(["revenue"])(accounts, range);
  const ctr = ratioPair(["clicks"], ["impressions"], 100)(accounts, range);
  const cpc = ratioPair(["spend", "cost"], ["clicks"], 1)(accounts, range);
  const costPerConversion = ratioPair(["spend", "cost"], ["conversions"], 1)(accounts, range);
  const roas = {
    current: revenue.current == null || spend.current == null || spend.current === 0
      ? null
      : revenue.current / spend.current,
    previous: revenue.previous == null || spend.previous == null || spend.previous === 0
      ? null
      : revenue.previous / spend.previous,
  };
  roas.change = percentChange(roas.current, roas.previous);

  return [
    kpi({ key: "spend", label: "Spend", help: HELP.spend, format: "money", tone: "neutral", accent: "amber" }, spend),
    kpi({ key: "impressions", label: "Impressions", help: HELP.impressions, accent: "indigo" }, impressions),
    kpi({ key: "clicks", label: "Clicks", help: HELP.clicks, accent: "sky" }, clicks),
    kpi({ key: "ctr", label: "CTR", help: HELP.ctr, format: "percent", accent: "violet" }, ctr),
    kpi({ key: "cpc", label: "CPC", help: HELP.cpc, format: "money", tone: "lower-better", accent: "rose" }, cpc),
    kpi({ key: "conversions", label: "Conversions", help: HELP.adsConversions, accent: "emerald" }, conversions),
    kpi({
      key: "costPerConversion",
      label: "Cost / conversion",
      help: HELP.costPerConversion,
      format: "money",
      tone: "lower-better",
      accent: "orange",
    }, costPerConversion),
    kpi({ key: "roas", label: "ROAS", help: HELP.roas, format: "roas", accent: "emerald" }, roas),
  ];
}

export function buildSocialKpis(accounts, range) {
  return [
    kpi({ key: "followers", label: "Followers", help: HELP.socialFollowers, accent: "indigo" }, stockPair(["followers"])(accounts, range)),
    kpi({ key: "reach", label: "Reach", help: HELP.socialReach, accent: "sky" }, flowPair(["reach"])(accounts, range)),
    kpi({ key: "views", label: "Views", help: HELP.views, accent: "violet" }, flowPair(["views", "impressions"])(accounts, range)),
    kpi({ key: "engagements", label: "Engagements", help: HELP.engagements, accent: "pink" }, flowPair(["engagements"])(accounts, range)),
  ];
}

export function buildWebsiteKpis(accounts, range) {
  return [
    kpi({ key: "users", label: "Users", help: HELP.users, accent: "indigo" }, flowPair(["users"])(accounts, range)),
    kpi({ key: "sessions", label: "Sessions", help: HELP.sessions, accent: "sky" }, flowPair(["sessions"])(accounts, range)),
    kpi({ key: "pageViews", label: "Page views", help: HELP.pageViews, accent: "violet" }, flowPair(["pageViews"])(accounts, range)),
    kpi({
      key: "bounceRate",
      label: "Bounce rate",
      help: HELP.bounceRate,
      format: "percent",
      tone: "lower-better",
      accent: "rose",
    }, weightedPair(["bounceRate"], ["sessions"])(accounts, range)),
    kpi({
      key: "averageSessionDuration",
      label: "Avg. session",
      help: HELP.averageSessionDuration,
      format: "duration",
      accent: "amber",
    }, weightedPair(["averageSessionDuration", "avgSessionDuration"], ["sessions"])(accounts, range)),
    kpi({ key: "conversions", label: "Conversions", help: HELP.webConversions, accent: "emerald" }, flowPair(["conversions"])(accounts, range)),
  ];
}

export function buildSearchKpis(accounts, range) {
  return [
    kpi({ key: "clicks", label: "Clicks", help: HELP.searchClicks, accent: "indigo" }, flowPair(["clicks"])(accounts, range)),
    kpi({ key: "impressions", label: "Impressions", help: HELP.searchImpressions, accent: "sky" }, flowPair(["impressions"])(accounts, range)),
    kpi({ key: "ctr", label: "CTR", help: HELP.searchCtr, format: "percent", accent: "violet" }, ratioPair(["clicks"], ["impressions"], 100)(accounts, range)),
    kpi({
      key: "position",
      label: "Position",
      help: HELP.position,
      format: "position",
      tone: "lower-better",
      accent: "amber",
    }, weightedPair(["position"], ["impressions"])(accounts, range)),
  ];
}

export function buildKpis(tab, accounts, range) {
  if (tab === "ads") return buildAdsKpis(accounts, range);
  if (tab === "social") return buildSocialKpis(accounts, range);
  if (tab === "website") return buildWebsiteKpis(accounts, range);
  if (tab === "search") return buildSearchKpis(accounts, range);
  return buildSummaryKpis(accounts, range);
}

export function formatCompact(value) {
  const number = Number(value || 0);
  if (Math.abs(number) >= 1_000_000) return `${(number / 1_000_000).toFixed(1)}M`;
  if (Math.abs(number) >= 1_000) return `${(number / 1_000).toFixed(1)}K`;
  return Math.round(number).toLocaleString();
}

export function formatMoney(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const number = Number(value);
  if (Math.abs(number) >= 10_000) return `$${formatCompact(number)}`;
  return `$${number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const remain = total % 60;
  if (minutes <= 0) return `${remain}s`;
  return `${minutes}m ${remain}s`;
}

export function formatKpiValue(stat) {
  if (stat?.current == null || Number.isNaN(Number(stat.current))) return "—";
  if (stat.format === "money") return formatMoney(stat.current);
  if (stat.format === "percent") return `${Number(stat.current).toFixed(2)}%`;
  if (stat.format === "duration") return formatDuration(stat.current);
  if (stat.format === "position") return Number(stat.current).toFixed(1);
  if (stat.format === "roas") return `${Number(stat.current).toFixed(2)}x`;
  return formatCompact(stat.current);
}

export function formatKpiNumber(stat, value) {
  return formatKpiValue({ ...stat, current: value });
}

export function kpiTooltip(stat, range) {
  const help = stat.help || stat.label;
  if (stat.change == null || stat.previous == null) {
    return `${help} Change versus the previous ${range?.days || ""} days appears when synced history covers both ranges.`;
  }
  const direction = stat.change > 0 ? "Up" : stat.change < 0 ? "Down" : "Unchanged";
  const amount = stat.change === 0 ? "" : ` ${Math.abs(stat.change).toFixed(1)}%`;
  return `${help} ${direction}${amount} versus the previous ${range.days} days (${formatKpiNumber(stat, stat.previous)} → ${formatKpiValue(stat)}).`;
}

export function buildInsights(kpis) {
  const ready = (kpis || []).filter((stat) => stat.current != null);
  const ordered = [
    ...ready.filter((stat) => stat.change != null),
    ...ready.filter((stat) => stat.change == null),
  ].slice(0, 3);

  return ordered.map((stat) => {
    const value = formatKpiValue(stat);
    if (stat.change == null) return `${stat.label} is ${value} in this period.`;
    if (Math.abs(stat.change) < 1) {
      return `${stat.label} is about the same as the previous period, at ${value}.`;
    }
    const direction = stat.change > 0 ? "up" : "down";
    return `${stat.label} is ${direction} ${Math.abs(stat.change).toFixed(0)}% versus the previous period, at ${value}.`;
  });
}

export function buildTodos(accounts) {
  const items = [];
  for (const account of accounts || []) {
    const connected = account.connectedAccount || {};
    if (connected.status === "disconnected") continue;
    const id = connected.id || connected.displayName || items.length;
    const name = connected.displayName || "Account";
    if (connected.syncPaused) {
      items.push({
        id: `pause-${id}`,
        accountId: connected.id || null,
        tone: "warning",
        title: `${name} sync is paused`,
        detail: "Resume sync from Data sources to pull new metrics.",
        action: "fix",
      });
      continue;
    }
    if (connected.status === "needs_reconnect" || connected.status === "error" || connected.lastError) {
      items.push({
        id: `fix-${id}`,
        accountId: connected.id || null,
        tone: "error",
        title: connected.status === "needs_reconnect" ? `Reconnect ${name}` : `Fix connection for ${name}`,
        detail: connected.lastError || "This account stopped syncing.",
        action: "fix",
      });
      continue;
    }
    if (account.metrics?.warning) {
      items.push({
        id: `warn-${id}`,
        accountId: connected.id || null,
        tone: "warning",
        title: name,
        detail: account.metrics.warning,
        action: "fix",
      });
      continue;
    }
    if (!account.metrics) {
      items.push({
        id: `sync-${id}`,
        accountId: connected.id || null,
        tone: "info",
        title: `Sync ${name}`,
        detail: "No metrics yet. Run a sync to fill this view.",
        action: "sync",
      });
    }
  }
  return items;
}

export function tabContentState(accounts) {
  if (!accounts?.length) return "empty";
  const broken = accounts.every((account) => {
    const connected = account.connectedAccount || {};
    return connected.status === "error"
      || connected.status === "needs_reconnect"
      || connected.lastError
      || connected.syncPaused;
  });
  if (broken) return "error";
  if (!accounts.some((account) => account.metrics)) return "unsynced";
  return "ready";
}

export function accountsForTrend(grouped, tab, metric) {
  if (tab === "ads") return grouped.ads;
  if (tab === "social") return grouped.social;
  if (tab === "website") return grouped.website;
  if (tab === "search") return grouped.search;
  if (metric === "visitors" || metric === "users") return grouped.website;
  if (metric === "reach" || metric === "followers") return grouped.social;
  if (metric === "adSpend" || metric === "spend") return grouped.ads;
  if (metric === "conversions") return [...grouped.website, ...grouped.ads];
  return [];
}

export function buildTrend(accounts, metric, range) {
  const dateSet = new Set();
  const lines = [];
  for (const account of accounts || []) {
    const points = firstPoints(account, seriesKeysForMetric(account, metric), range);
    if (!points.length) continue;
    const byDate = new Map();
    for (const point of points) {
      const day = dayKey(point.date);
      byDate.set(day, (byDate.get(day) || 0) + Number(point.value || 0));
      dateSet.add(day);
    }
    lines.push({
      id: account.connectedAccount?.id || `line-${lines.length}`,
      label: account.connectedAccount?.displayName || "Account",
      byDate,
    });
  }
  const dates = [...dateSet].sort();
  return {
    dates,
    lines: lines.map((line) => ({
      id: line.id,
      label: line.label,
      values: dates.map((day) => (line.byDate.has(day) ? line.byDate.get(day) : null)),
    })),
  };
}
